import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
 import { Loader2, Users, Crown, CreditCard, TrendingUp, UserPlus, Shield, Activity, RefreshCw, Trophy, Ticket } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { useUserStats, TIMEFRAMES } from "@/hooks/useUserStats";
import { useAdminStatsGeneration } from "@/hooks/useAdminStatsGeneration";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const CHART_COLORS = [
  "hsl(var(--secondary))",
  "hsl(var(--primary))",
  "hsl(var(--info))",
  "hsl(var(--warning))",
  "hsl(var(--destructive))",
];

export const UserStatsCard = () => {
  const { timeframe, setTimeframe, stats, chartData, isLoading, timeframeLabel, generatedAt, isRefreshing } = useUserStats();
  const { generateStats, isGenerating } = useAdminStatsGeneration();

  const hasNoCache = !isLoading && !stats;

  // Auto-generate stats on first load if no cache exists
  useEffect(() => {
    if (hasNoCache && !isGenerating("user")) {
      generateStats("user");
    }
  }, [hasNoCache]);

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-secondary" />
              User Stats
            </CardTitle>
            <CardDescription className="text-xs">
              {timeframeLabel}
              {generatedAt && (
                <span className="ml-2 text-muted-foreground">
                  · Updated {formatDistanceToNow(new Date(generatedAt))} ago
                  {isRefreshing && <span className="ml-1 text-xs opacity-70">(refreshing...)</span>}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => generateStats("user")}
              disabled={isGenerating("user") || isRefreshing}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", (isGenerating("user") || isRefreshing) && "animate-spin")} />
            </Button>
            <div className="flex gap-0.5">
              {TIMEFRAMES.map(({ value, label }) => (
                <Button
                  key={value}
                  variant={timeframe === value ? "secondary" : "ghost"}
                  size="sm"
                  className={cn("h-6 px-2 text-xs", timeframe === value && "font-bold")}
                  onClick={() => setTimeframe(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="py-3 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : hasNoCache ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-muted-foreground mb-4">No cached stats available</p>
            <Button onClick={() => generateStats("user")} disabled={isGenerating("user")}>
              {isGenerating("user") ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Stats"
              )}
            </Button>
          </div>
        ) : (
          <>
            {/* Stats Grid - Inline compact */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Total:</span>
                <span className="font-semibold">{stats?.total ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Active:</span>
                <span className="font-semibold">{stats?.activeUsers ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Total Credits:</span>
                <span className="font-semibold">{stats?.totalCredits ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">With Credits:</span>
                <span className="font-semibold">{stats?.usersWithCredits ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Crown className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Unlimited:</span>
                <span className="font-semibold">{stats?.unlimitedActive ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Admins:</span>
                <span className="font-semibold">{stats?.admins ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Hosts:</span>
                <span className="font-semibold">{stats?.hosts ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <UserPlus className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Referred:</span>
                <span className="font-semibold">{stats?.referredUsers ?? 0}</span>
              </div>
            </div>

            {/* Charts */}
           <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {/* New Users Over Time */}
              <div className="lg:col-span-2">
                <h4 className="text-xs font-medium text-muted-foreground mb-1">New Users</h4>
                <div className="h-28">
                  {chartData.usersOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.usersOverTime} margin={{ left: 0, right: 4 }}>
                        <XAxis
                          dataKey="date"
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          interval="preserveStartEnd"
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                        />
                        <YAxis
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                          width={24}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "11px",
                          }}
                        />
                        <Bar dataKey="users" fill="hsl(var(--secondary))" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

              {/* Member Types Breakdown */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Member Types</h4>
                <div className="h-28">
                  {chartData.memberTypes.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.memberTypes}
                          dataKey="count"
                          nameKey="type"
                          cx="30%"
                          cy="50%"
                          outerRadius={35}
                        >
                          {chartData.memberTypes.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend
                          layout="vertical"
                          align="right"
                          verticalAlign="middle"
                          wrapperStyle={{ fontSize: "10px", paddingLeft: "8px" }}
                          formatter={(value, entry: any) => `${value}: ${entry.payload.count}`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "11px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

             {/* Top Referrers Leaderboard */}
             <div>
               <h4 className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                 <Trophy className="h-3 w-3" />
                 Top Referrers
               </h4>
               <div className="h-28 overflow-y-auto">
                 {chartData.topReferrers && chartData.topReferrers.length > 0 ? (
                   <div className="space-y-1">
                     {chartData.topReferrers.slice(0, 5).map((referrer, index) => (
                       <div key={referrer.id} className="flex items-center justify-between text-xs py-0.5 px-1 rounded hover:bg-muted/50">
                         <div className="flex items-center gap-1.5 min-w-0">
                           <span className={cn(
                             "font-bold w-4 text-center",
                             index === 0 && "text-warning",
                             index === 1 && "text-muted-foreground",
                            index === 2 && "text-orange-600 dark:text-orange-400"
                           )}>
                             {index + 1}
                           </span>
                           <span className="truncate max-w-[80px]" title={referrer.name}>{referrer.name}</span>
                         </div>
                         <div className="flex items-center gap-2 text-muted-foreground shrink-0">
                           <span className="flex items-center gap-0.5">
                             <UserPlus className="h-2.5 w-2.5" />
                             {referrer.referralCount}
                           </span>
                           <span className="flex items-center gap-0.5" title={`${referrer.referredUsersBooked} referred users booked ${referrer.totalEventsBooked} events`}>
                             <Ticket className="h-2.5 w-2.5" />
                             {referrer.totalEventsBooked}
                           </span>
                         </div>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No referrers yet</div>
                 )}
               </div>
             </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
